import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

const AnimatedMessage = ({ content, onComplete, isLastMessage, isNewMessage }) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(!isLastMessage || !isNewMessage);
  const wordsRef = useRef([]);
  const currentIndexRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // If not the last message or not a new message, show full content immediately
    if (!isLastMessage || !isNewMessage) {
      setDisplayedContent(content);
      setIsComplete(true);
      onComplete?.();
      return;
    }

    // Reset state for animation
    setDisplayedContent('');
    setIsComplete(false);
    currentIndexRef.current = 0;

    // Split content into words, preserving markdown syntax
    const words = content.split(/(\s+)/).filter(Boolean);
    wordsRef.current = words;

    const animateText = () => {
      if (currentIndexRef.current < wordsRef.current.length) {
        const currentWord = wordsRef.current[currentIndexRef.current];
        const isMarkdownSyntax = /[*#\[\]()_`]/.test(currentWord);

        // Update displayed content
        setDisplayedContent(prev => prev + currentWord);
        currentIndexRef.current += 1;

        // Calculate delay based on word type
        const delay = isMarkdownSyntax ? 0 : 30; // Faster animation

        timeoutRef.current = setTimeout(animateText, delay);
      } else {
        setIsComplete(true);
        onComplete?.();
      }
    };

    timeoutRef.current = setTimeout(animateText, 0);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [content, onComplete, isLastMessage, isNewMessage]);

  // Show full content if user clicks on the message
  const handleClick = () => {
    if (!isComplete) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setDisplayedContent(content);
      setIsComplete(true);
      onComplete?.();
    }
  };

  return (
    <div onClick={handleClick} style={{ cursor: isComplete ? 'default' : 'pointer' }} className="animated-message">
      <ReactMarkdown>
        {displayedContent}
      </ReactMarkdown>
    </div>
  );
};

export default AnimatedMessage; 